/**
 * The Search view's filter controls — all free, all instant.
 *
 *   - a name box (typing filters; it never runs the billed search);
 *   - type pills, always visible (the file dialogs' kinds, from one table);
 *   - area pills: Documents, Arche Saves, My-Fias — all on by default;
 *   - "More": size and date bounds, behind one button;
 *   - a chip for every active filter, so nothing applied is hidden in a
 *     closed popover.
 */
import { Filter, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { VaultAreaDto } from '@/lib/api';
import {
  VAULT_FILE_KINDS,
  VAULT_FILE_KIND_LABELS,
  type VaultFileKind,
} from '@/lib/vault-file-kinds';
import { AREA_NAMES } from '../../lib/locations';
import {
  AGE_PRESETS,
  ALL_AREAS,
  EMPTY_SEARCH_FILTERS,
  SIZE_PRESETS,
  describeSearchFilters,
  moreFiltersActive,
  type AgePreset,
  type SearchFilterState,
  type SizePreset,
} from './search-filters';

function Pill({
  pressed,
  onClick,
  children,
  title,
}: {
  pressed: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      title={title}
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
        pressed
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent',
      )}
    >
      {children}
    </button>
  );
}

function PresetSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | '';
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T | '') => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select
        className="h-7 rounded-md border bg-background px-1 text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value as T | '')}
      >
        <option value="">Any</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const AREA_HINTS: Record<VaultAreaDto, string> = {
  documents: 'Your own files and folders',
  'arche-saves': 'Files your arches delivered',
  'my-fias': 'Files your arches keep for themselves',
};

export function SearchFilterBar({
  state,
  onChange,
}: {
  state: SearchFilterState;
  onChange: (next: SearchFilterState) => void;
}) {
  const toggleKind = (kind: VaultFileKind) =>
    onChange({
      ...state,
      kinds: state.kinds.includes(kind)
        ? state.kinds.filter((k) => k !== kind)
        : [...state.kinds, kind],
    });
  const toggleArea = (area: VaultAreaDto) =>
    onChange({
      ...state,
      areas: state.areas.includes(area)
        ? state.areas.filter((a) => a !== area)
        : ALL_AREAS.filter((a) => a === area || state.areas.includes(a)),
    });
  const more = moreFiltersActive(state);
  const chips = describeSearchFilters(state, onChange);

  return (
    <div className="flex flex-col gap-2">
      <div role="group" className="flex flex-wrap items-center gap-1.5" aria-label="File type">
        <Pill pressed={state.kinds.length === 0} onClick={() => onChange({ ...state, kinds: [] })}>
          All types
        </Pill>
        {VAULT_FILE_KINDS.map((kind) => (
          <Pill key={kind} pressed={state.kinds.includes(kind)} onClick={() => toggleKind(kind)}>
            {VAULT_FILE_KIND_LABELS[kind]}
          </Pill>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* The area "Documents" and the file type "Documents" share a word;
            the groups' names keep them apart for everyone. */}
        <div role="group" aria-label="Area" className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground" aria-hidden>
            In
          </span>
          {ALL_AREAS.map((area) => (
            <Pill
              key={area}
              pressed={state.areas.includes(area)}
              onClick={() => toggleArea(area)}
              title={AREA_HINTS[area]}
            >
              {AREA_NAMES[area]}
            </Pill>
          ))}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="More filters"
              aria-pressed={more}
              title="More filters — size and dates"
              className={cn(
                'relative ml-1 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                more
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent',
              )}
            >
              <Filter className="h-3 w-3" aria-hidden /> More
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 space-y-3 p-3">
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Size
              </p>
              <PresetSelect<Exclude<SizePreset, ''>>
                label="Larger than"
                value={state.largerThan}
                options={SIZE_PRESETS}
                onChange={(largerThan) => onChange({ ...state, largerThan })}
              />
              <PresetSelect<Exclude<SizePreset, ''>>
                label="Smaller than"
                value={state.smallerThan}
                options={SIZE_PRESETS}
                onChange={(smallerThan) => onChange({ ...state, smallerThan })}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Modified
              </p>
              <PresetSelect<Exclude<AgePreset, ''>>
                label="In the last"
                value={state.modifiedNewerThan}
                options={AGE_PRESETS}
                onChange={(modifiedNewerThan) => onChange({ ...state, modifiedNewerThan })}
              />
              <PresetSelect<Exclude<AgePreset, ''>>
                label="Not for"
                value={state.modifiedOlderThan}
                options={AGE_PRESETS}
                onChange={(modifiedOlderThan) => onChange({ ...state, modifiedOlderThan })}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Added
              </p>
              <PresetSelect<Exclude<AgePreset, ''>>
                label="In the last"
                value={state.addedNewerThan}
                options={AGE_PRESETS}
                onChange={(addedNewerThan) => onChange({ ...state, addedNewerThan })}
              />
              <PresetSelect<Exclude<AgePreset, ''>>
                label="Over … ago"
                value={state.addedOlderThan}
                options={AGE_PRESETS}
                onChange={(addedOlderThan) => onChange({ ...state, addedOlderThan })}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {chips.length > 0 && (
        <div
          role="group"
          className="flex flex-wrap items-center gap-1.5"
          aria-label="Active filters"
        >
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.clear}
                aria-label={`Remove filter: ${chip.label}`}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {chips.length > 1 && (
            <button
              type="button"
              className="text-xs text-muted-foreground underline"
              onClick={() => onChange({ ...EMPTY_SEARCH_FILTERS, areas: [...ALL_AREAS] })}
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}
